import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import LanguageToggle from './LanguageToggle';
import { DirectoryDropdown, DirectoryMobileSection } from './DirectoryMenu';
import { useAuth } from '../../hooks/useAuth';

const SCROLL_THRESHOLD = 24;

// Thin line between the sections of the desktop bar.
function Divider() {
  return <span aria-hidden className="w-px h-5 bg-ink/15 shrink-0" />;
}

export default function AppNav() {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isSuperRoute = location.pathname.startsWith('/super');
  const { user, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let ticking = false;
    const update = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
      ticking = false;
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleLogout = async () => {
    await signOut();
    setMenuOpen(false);
    navigate('/');
  };

  const closeMenu = () => setMenuOpen(false);
  const compactBrand = scrolled || isAdminRoute;

  const desktopLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'shrink-0 whitespace-nowrap text-sm py-3 text-ink font-semibold border-b-2 border-ink'
      : `shrink-0 whitespace-nowrap text-sm py-3 ${
          scrolled ? 'text-ink/75' : 'text-ash'
        } hover:text-ink transition-colors border-b-2 border-transparent`;

  const mobileLinkClass = ({ isActive }: { isActive: boolean }) =>
    isActive
      ? 'flex items-center min-h-14 text-ink font-semibold'
      : 'flex items-center min-h-14 text-ink/70 hover:text-ink transition-colors';

  const panelLink =
    user && !isAdminRoute && !isSuperRoute ? (
      user.role === 'superAdmin' ? (
        <Link to="/super" onClick={closeMenu} className="text-sm text-ink hover:underline">
          {t('admin.superAdminTitle')}
        </Link>
      ) : user.role === 'editor' ? (
        <Link to="/admin" onClick={closeMenu} className="text-sm text-ink hover:underline">
          {t('directory.admin.myItems')}
        </Link>
      ) : null
    ) : null;

  // Desktop pill: panel link only when signed in (Login / email / logout live in Footer).
  const desktopAuthControls = panelLink;

  // Mobile menu: panel link when signed in; email + logout for convenience while menu covers the footer.
  const mobileAuthControls = user ? (
    <>
      {panelLink}
      <span className="text-sm text-ash truncate max-w-[10rem]">{user.email}</span>
      <button
        type="button"
        onClick={handleLogout}
        className="text-sm text-ink hover:opacity-70 transition-opacity"
      >
        {t('admin.logout')}
      </button>
    </>
  ) : null;

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 flex justify-center px-6 pointer-events-none">
        {/* Top gap + height set where the bar ends: keep --nav-bottom in index.css in step. */}
        <div
          className={`pointer-events-auto mt-6 w-full rounded-full border flex items-center px-5 sm:px-7 transition-[max-width,height,background-color,box-shadow,border-color] duration-300 ease-out ${
            scrolled
              ? 'max-w-3xl h-14 bg-paper/55 backdrop-blur-xl backdrop-saturate-150 border-white/60 shadow-[0_2px_6px_rgba(0,0,0,.04),0_6px_18px_rgba(0,0,0,.10)]'
              : 'max-w-6xl h-16 sm:h-[72px] bg-paper/70 backdrop-blur-md border-transparent shadow-none'
          }`}
        >
          <Link
            to="/"
            onClick={closeMenu}
            aria-label={`${t('common.appName')} – ${t('nav.home')}`}
            className="flex flex-col text-ink hover:opacity-90 transition-opacity shrink-0 sm:pe-7"
          >
            <span
              className={`font-bold tracking-tight whitespace-nowrap leading-none transition-[font-size] duration-300 ease-out ${
                compactBrand ? 'text-lg' : 'text-xl sm:text-[22px]'
              }`}
            >
              {t('common.appName')}
            </span>
            {/* Same name in the other language, small, under the main one. Hidden when the bar shrinks. */}
            <span
              aria-hidden
              lang={isHebrew ? 'en' : 'he'}
              className={`block overflow-hidden whitespace-nowrap text-[11px] leading-none text-ash transition-[max-height,opacity,margin] duration-300 ease-out ${
                isHebrew ? 'tracking-[0.16em] uppercase' : ''
              } ${compactBrand ? 'max-h-0 opacity-0 mt-0' : 'max-h-4 opacity-100 mt-1.5'}`}
            >
              {t('common.appNameSub')}
            </span>
          </Link>

          <span aria-hidden className="hidden sm:block w-px h-7 bg-ink/15 shrink-0" />

          <nav className="hidden sm:flex flex-1 items-center justify-evenly">
            <NavLink to="/" end className={desktopLinkClass}>
              {t('nav.home')}
            </NavLink>
            <Divider />
            <NavLink to="/shuls" className={desktopLinkClass}>
              {t('nav.shuls')}
            </NavLink>
            <Divider />
            <NavLink to="/minyanim" className={desktopLinkClass}>
              {t('nav.minyanSearch')}
            </NavLink>
            <Divider />
            <NavLink to="/calendar" className={desktopLinkClass}>
              {t('nav.calendar')}
            </NavLink>
            <Divider />
            <DirectoryDropdown />
          </nav>

          {desktopAuthControls && (
            <div className="hidden sm:flex items-center gap-4 ms-6 shrink-0">
              <Divider />
              {desktopAuthControls}
            </div>
          )}

          <button
            type="button"
            className="sm:hidden ms-auto text-ink hover:opacity-70 transition-opacity p-2 -me-2"
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-40 sm:hidden bg-paper flex flex-col"
          role="dialog"
          aria-modal="true"
        >
          {/* Clearance for the floating pill + X sitting above this panel */}
          <div className="h-24 shrink-0" aria-hidden />
          <nav className="flex-1 overflow-y-auto px-6">
            <>
              <NavLink to="/" end className={mobileLinkClass} onClick={closeMenu}>
                {t('nav.home')}
              </NavLink>
              <div className="border-b border-ink/10" />
              <NavLink to="/shuls" className={mobileLinkClass} onClick={closeMenu}>
                {t('nav.shuls')}
              </NavLink>
              <div className="border-b border-ink/10" />
              <NavLink to="/minyanim" className={mobileLinkClass} onClick={closeMenu}>
                {t('nav.minyanSearch')}
              </NavLink>
              <div className="border-b border-ink/10" />
              <NavLink to="/calendar" className={mobileLinkClass} onClick={closeMenu}>
                {t('nav.calendar')}
              </NavLink>
              <div className="border-b border-ink/10" />
              <DirectoryMobileSection onNavigate={closeMenu} />
              <div className="border-b border-ink/10" />
              <NavLink to="/contact" className={mobileLinkClass} onClick={closeMenu}>
                {t('nav.contact')}
              </NavLink>
              <div className="border-b border-ink/10" />
            </>
          </nav>
          <div className="px-6 py-5 border-t border-ink/10 flex flex-col gap-3">
            <LanguageToggle />
            {mobileAuthControls && (
              <div className="flex items-center gap-3 flex-wrap">{mobileAuthControls}</div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
